import { FormControl, Grid, MenuItem, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { useState } from "react";
import { SelectLight } from "../../../common/components/styled/styled";
import { BackgroundJobForm } from "./BackgroundJobForm";
import {
  acceptWorkAgreement,
  acceptWorkOffer,
  approvePayslips,
  autoApproveEntries,
  cancelShift,
  createGarnishmentPayment,
  createHolidayBacklogReports,
  createInvoices,
  createPayerSummaryReportDelivery,
  createPayslips,
  createSalaryPayment,
  createTaxPayment,
  createTradeUnionPayment,
  createWageReportDelivery,
  deleteInvoice,
  deleteWageReportDelivery,
  exportGarnishmentPayment,
  exportTaxPayment,
  exportTradeUnionPayment,
  lockInvoices,
  lockPayslips,
  processWorkOffers,
  rebuildWorkHistoryTimes,
  recalculateCustomerPricing,
  redactTalentData,
  sendInvoices,
  updateInvoice,
  updatePayerSummaryReportDeliveryStatus,
  updateWageReportDeliveryStatus,
} from "../settingsActions";
import { isProdEnv } from "../../../common/helpers/envHelpers";
import { useAppDispatch } from "../../../app/hooks";
import { format } from "date-fns";
import { PayloadAction, isFulfilled } from "@reduxjs/toolkit";
import useMessage from "../../../hooks/useMessage";
import { TestingUtilitiesTitle } from "../styles";
import { fetchAssignmentById } from "../../talents/talentsSlice";
import { AcceptWorkOffer } from "../settingsApi";

export const BackgroundJobsPanel = () => {
  const { t } = useTranslation("settings");
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();

  const isLoading = false;

  const [action, setAction] = useState<string>("");

  const defaultValues = {
    schedulingClass: { id: "", name: "" },
    date: null,
    wave: "",
    range: [null, null],
    contractId: "",
    eezyCompanyId: "",
    wageReportDeliveryId: "",
    assignmentId: "",
    paySalary: false,
    reason: "",
    talentId: "",
    version: "",
    status: "",
    invoiceId: "",
    bundleId: "",
    workOffersId: "",
    startingShiftAt: "",
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const ACTIONS_PREPROD_ONLY = ["redactTalentData"];

  const ACTIONS = [
    "createInvoices",
    "lockInvoices",
    "sendInvoices",
    "createPayslips",
    "approvePayslips",
    "lockPayslips",
    "createHolidayBacklogReports",
    "autoApproveEntries",
    "recalculateCustomerPricing",
    "createSalaryPayment",
    "createTaxPayment",
    "exportTaxPayment",
    "createGarnishmentPayment",
    "exportGarnishmentPayment",
    "createTradeUnionPayment",
    "exportTradeUnionPayment",
    "createPayerSummaryReportDelivery",
    "updatePayerSummaryReportDeliveryStatus",
    "createWageReportDelivery",
    "updateWageReportDeliveryStatus",
    "deleteWageReportDelivery",
    "acceptWorkAgreement",
    "acceptWorkOffer",
    "processWorkOffers",
    "cancelAssignment",
    "updateInvoice",
    "deleteInvoice",
    "rebuildWorkHistoryTimes",
  ].concat(!isProdEnv() ? ACTIONS_PREPROD_ONLY : []);

  const { reset } = methods;

  const selectAction = (value: string) => {
    setAction(value);
  };

  const handleResult = (
    result: PayloadAction<any>,
    submittedValues: any = {},
  ) => {
    if (isFulfilled(result)) {
      sendMessage(t("commandSuccess"), "success");
      reset({ ...defaultValues, ...submittedValues });
    }
  };

  const onSubmit = (fieldValues: FieldValues) => {
    if (action === undefined) return;
    switch (action) {
      case "createInvoices":
        dispatch(
          createInvoices({
            schedulingClassId: fieldValues.schedulingClass,
            date: format(fieldValues.date, "yyyy-MM-dd"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "lockInvoices":
        dispatch(
          lockInvoices({
            schedulingClassId: fieldValues.schedulingClass,
            date: format(fieldValues.date, "yyyy-MM-dd"),
            wave: fieldValues.wave,
          }),
        ).then((result) => handleResult(result));
        break;
      case "sendInvoices":
        dispatch(
          sendInvoices({
            schedulingClassId: fieldValues.schedulingClass,
            date: format(fieldValues.date, "yyyy-MM-dd"),
            wave: fieldValues.wave,
          }),
        ).then((result) => handleResult(result));
        break;
      case "createPayslips":
        dispatch(
          createPayslips({
            schedulingClassId: fieldValues.schedulingClass,
            date: format(fieldValues.date, "yyyy-MM-dd"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "approvePayslips":
        dispatch(
          approvePayslips({
            schedulingClassId: fieldValues.schedulingClass,
            date: format(fieldValues.date, "yyyy-MM-dd"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "lockPayslips":
        dispatch(
          lockPayslips({
            schedulingClassId: fieldValues.schedulingClass,
            date: format(fieldValues.date, "yyyy-MM-dd"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "createHolidayBacklogReports":
        dispatch(
          createHolidayBacklogReports({
            date: format(fieldValues.date, "yyyy-MM-dd"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "redactTalentData":
        dispatch(
          redactTalentData({
            talentId: fieldValues.talentId,
          }),
        ).then((result) => handleResult(result));
        break;
      case "autoApproveEntries":
        dispatch(
          autoApproveEntries({
            periodClass: fieldValues.schedulingClass,
            range: {
              start: format(fieldValues.range[0], "yyyy-MM-dd"),
              end: format(fieldValues.range[1], "yyyy-MM-dd"),
            },
          }),
        ).then((result) => handleResult(result));
        break;
      case "rebuildWorkHistoryTimes":
        dispatch(
          rebuildWorkHistoryTimes({
            talentId: fieldValues.talentId,
            startDate: format(fieldValues.range[0], "yyyy-MM-dd"),
            endDate: format(fieldValues.range[1], "yyyy-MM-dd"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "recalculateCustomerPricing":
        dispatch(
          recalculateCustomerPricing({
            contractId: fieldValues.contractId,
            periodDate: format(fieldValues.date, "yyyy-MM-dd"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "createSalaryPayment":
        dispatch(
          createSalaryPayment({
            eezyCompanyId: fieldValues.eezyCompanyId,
            schedulingClassId: fieldValues.schedulingClass,
            date: format(fieldValues.date, "yyyy-MM-dd"),
            numberOfPayslips: fieldValues.numberOfPayslips,
          }),
        ).then((result) => handleResult(result));
        break;
      case "createTaxPayment":
        dispatch(
          createTaxPayment({
            eezyCompanyId: fieldValues.eezyCompanyId,
            date: format(fieldValues.date, "yyyy-MM-dd"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "exportTaxPayment":
        dispatch(
          exportTaxPayment({
            eezyCompanyId: fieldValues.eezyCompanyId,
            date: format(fieldValues.date, "yyyy-MM-dd"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "createGarnishmentPayment":
        dispatch(
          createGarnishmentPayment({
            eezyCompanyId: fieldValues.eezyCompanyId,
            schedulingClassId: fieldValues.schedulingClass,
            date: format(fieldValues.date, "yyyy-MM-dd"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "exportGarnishmentPayment":
        dispatch(
          exportGarnishmentPayment({
            eezyCompanyId: fieldValues.eezyCompanyId,
            schedulingClassId: fieldValues.schedulingClass,
            date: format(fieldValues.date, "yyyy-MM-dd"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "createTradeUnionPayment":
        dispatch(
          createTradeUnionPayment({
            eezyCompanyId: fieldValues.eezyCompanyId,
            date: format(fieldValues.date, "yyyy-MM-dd"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "exportTradeUnionPayment":
        dispatch(
          exportTradeUnionPayment({
            eezyCompanyId: fieldValues.eezyCompanyId,
            date: format(fieldValues.date, "yyyy-MM-dd"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "createPayerSummaryReportDelivery":
        dispatch(
          createPayerSummaryReportDelivery({
            eezyCompanyId: fieldValues.eezyCompanyId,
            reportMonth: format(fieldValues.date, "yyyy-MM"),
          }),
        ).then((result) => handleResult(result));
        break;
      case "updatePayerSummaryReportDeliveryStatus":
        dispatch(
          updatePayerSummaryReportDeliveryStatus({
            payerSummaryReportDeliveryId:
              fieldValues.payerSummaryReportDeliveryId,
          }),
        ).then((result) => handleResult(result));
        break;
      case "createWageReportDelivery":
        dispatch(
          createWageReportDelivery({
            payslipId: fieldValues.payslipId,
          }),
        ).then((result) => handleResult(result));
        break;
      case "updateWageReportDeliveryStatus":
        dispatch(
          updateWageReportDeliveryStatus({
            wageReportDeliveryId: fieldValues.wageReportDeliveryId,
          }),
        ).then((result) => handleResult(result));
        break;
      case "deleteWageReportDelivery":
        dispatch(
          deleteWageReportDelivery({
            wageReportDeliveryId: fieldValues.wageReportDeliveryId,
          }),
        ).then((result) => handleResult(result));
        break;
      case "acceptWorkAgreement":
        dispatch(
          acceptWorkAgreement({
            id: fieldValues.workAgreementId,
            version: fieldValues.version,
          }),
        ).then((result) => handleResult(result));
        break;
      case "cancelAssignment":
        dispatch(fetchAssignmentById(fieldValues.assignmentId)).then(
          (result) => {
            if (isFulfilled(result)) {
              const assignment = result.payload;
              if (assignment.bundleId) {
                sendMessage(t("assignmentHasOrder"), "error");
                return;
              } else {
                dispatch(
                  cancelShift({
                    assignmentId: fieldValues.assignmentId,
                    paySalary: fieldValues.paySalary,
                    reason: fieldValues.reason,
                  }),
                ).then((result) => handleResult(result));
              }
            }
          },
        );
        break;

      case "updateInvoice":
        dispatch(
          updateInvoice({
            invoiceId: fieldValues.invoiceId,
          }),
        ).then((result) => handleResult(result));
        break;

      case "acceptWorkOffer":
        var acceptWorkOfferPayload: AcceptWorkOffer = {
          bundleId: fieldValues.bundleId,
          talentId: fieldValues.talentId,
        };

        const startingShiftAt = fieldValues.startingShiftAt;
        if (startingShiftAt) {
          const epochMilliSeconds = new Date(startingShiftAt).getTime();
          acceptWorkOfferPayload = {
            ...acceptWorkOfferPayload,
            startingShiftAt: epochMilliSeconds.toString(),
          };
        }
        dispatch(acceptWorkOffer(acceptWorkOfferPayload)).then((result) =>
          handleResult(result),
        );
        break;

      case "processWorkOffers":
        dispatch(
          processWorkOffers({
            workOffersId: fieldValues.workOffersId,
          }),
        ).then((result) => handleResult(result));
        break;

      case "deleteInvoice":
        dispatch(
          deleteInvoice({
            invoiceId: fieldValues.invoiceId,
          }),
        ).then((result) => handleResult(result));
        break;

      default:
        break;
    }
  };

  return (
    <>
      <TestingUtilitiesTitle>{t("backgroundJobs")}</TestingUtilitiesTitle>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={2} justifyContent="flex-end" alignItems="center">
          <Typography textAlign="right">{t("settings:command")}</Typography>
        </Grid>
        <Grid item xs={10} justifyContent="flex-end">
          <FormControl fullWidth>
            <SelectLight
              size="small"
              value={action}
              onChange={(event) => selectAction(event.target.value as string)}
              sx={{ maxWidth: "450px" }}
            >
              {ACTIONS.map((action) => (
                <MenuItem key={action} value={action}>
                  {t(action)}
                </MenuItem>
              ))}
            </SelectLight>
          </FormControl>
        </Grid>

        <FormProvider {...methods}>
          <BackgroundJobForm
            action={action}
            onSubmit={onSubmit}
            isLoading={isLoading}
          />
        </FormProvider>
      </Grid>
    </>
  );
};
